"use client";

// Chart.js renderer for a saved chart spec. Shared by the builder's live
// preview and the /charts/<id> share page: both re-read the live metrics API
// client-side (the spec is all the server stores). Canvas colours resolve
// CSS variables at runtime — see community-stats/charts.tsx resolveColor.
import { c as _c } from "react/compiler-runtime";
import { Chart as ChartJS, BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, type ChartOptions } from "chart.js";
import { Bar, Scatter } from "react-chartjs-2";
import { useEffect, useState } from "react";
import { useT } from "@/lib/i18n";
import { buildChartRows, fetchMetricRows, fetchNameMap, type SavedChartSpec } from "@/lib/saved-chart-spec";
import { METRIC_LABELS, metricUnit } from "@/lib/chart-metric-labels";
ChartJS.register(BarElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip);
function resolveColor(color: string): string {
  if (!color.startsWith("var(")) return color;
  if (typeof window === "undefined") return "";
  return getComputedStyle(document.documentElement).getPropertyValue(color.slice(4, -1)).trim();
}
function axisTitle(key: string, t: (s: string) => string): string {
  const label = METRIC_LABELS[key as keyof typeof METRIC_LABELS] ?? key;
  const unit = metricUnit(key);
  return unit ? `${t(label)} (${unit})` : t(label);
}
const BAR_COLOR = "var(--accent-gold)";
const TEXT_MUTED = "var(--text-muted)";
export default function SavedChartView(t0) {
  const $ = _c(101);
  const {
    spec,
    height: t1,
    includeUpgraded: t2
  } = t0;
  const height = t1 === undefined ? 420 : t1;
  const includeUpgraded = t2 === undefined ? false : t2;
  const t = useT();
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      loading: true,
      rows: []
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  const [state, setState] = useState(t3);
  let t4;
  let t5;
  if ($[1] !== includeUpgraded || $[2] !== spec) {
    t4 = () => {
      let alive = true;
      setState(_temp);
      const timer = setTimeout(() => {
        Promise.all([fetchMetricRows(spec.source, spec.bracket, spec.character), fetchNameMap(spec.source)]).then(t6 => {
          const [rows, names] = t6;
          if (!alive) {
            return;
          }
          setState({
            loading: false,
            rows: buildChartRows(rows, names, spec, {
              includeUpgraded
            })
          });
        });
      }, 250);
      return () => {
        alive = false;
        clearTimeout(timer);
      };
    };
    t5 = [spec, includeUpgraded];
    $[1] = includeUpgraded;
    $[2] = spec;
    $[3] = t4;
    $[4] = t5;
  } else {
    t4 = $[3];
    t5 = $[4];
  }
  useEffect(t4, t5);
  if (state.loading) {
    let t6;
    if ($[5] !== height) {
      t6 = {
        height
      };
      $[5] = height;
      $[6] = t6;
    } else {
      t6 = $[6];
    }
    let t7;
    if ($[7] !== t) {
      t7 = t("Loading");
      $[7] = t;
      $[8] = t7;
    } else {
      t7 = $[8];
    }
    let t8;
    if ($[9] !== t6 || $[10] !== t7) {
      t8 = <div className="flex items-center justify-center text-sm text-[var(--text-muted)]" style={t6}>{t7}</div>;
      $[9] = t6;
      $[10] = t7;
      $[11] = t8;
    } else {
      t8 = $[11];
    }
    return t8;
  }
  if (state.rows.length === 0) {
    let t6;
    if ($[12] !== height) {
      t6 = {
        height
      };
      $[12] = height;
      $[13] = t6;
    } else {
      t6 = $[13];
    }
    let t7;
    if ($[14] !== t) {
      t7 = t("No data for this selection.");
      $[14] = t;
      $[15] = t7;
    } else {
      t7 = $[15];
    }
    let t8;
    if ($[16] !== t6 || $[17] !== t7) {
      t8 = <div className="flex items-center justify-center text-sm text-[var(--text-muted)]" style={t6}>{t7}</div>;
      $[16] = t6;
      $[17] = t7;
      $[18] = t8;
    } else {
      t8 = $[18];
    }
    return t8;
  }
  let t6;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = resolveColor(BAR_COLOR) || "#c8a24a";
    $[19] = t6;
  } else {
    t6 = $[19];
  }
  const accent = t6;
  let t7;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = resolveColor(TEXT_MUTED) || "#888";
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  const muted = t7;
  if (spec.chart === "scatter") {
    let t8;
    if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: _temp2
          }
        }
      };
      $[21] = t8;
    } else {
      t8 = $[21];
    }
    let t9;
    if ($[22] !== spec.x || $[23] !== t) {
      t9 = axisTitle(spec.x, t);
      $[22] = spec.x;
      $[23] = t;
      $[24] = t9;
    } else {
      t9 = $[24];
    }
    let t10;
    if ($[25] !== t9) {
      t10 = {
        display: true,
        text: t9,
        color: muted
      };
      $[25] = t9;
      $[26] = t10;
    } else {
      t10 = $[26];
    }
    let t11;
    let t12;
    if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
      t11 = {
        color: muted
      };
      t12 = {
        color: "rgba(128,128,128,0.15)"
      };
      $[27] = t11;
      $[28] = t12;
    } else {
      t11 = $[27];
      t12 = $[28];
    }
    let t13;
    if ($[29] !== t10) {
      t13 = {
        title: t10,
        ticks: t11,
        grid: t12
      };
      $[29] = t10;
      $[30] = t13;
    } else {
      t13 = $[30];
    }
    let t14;
    if ($[31] !== spec.y || $[32] !== t) {
      t14 = axisTitle(spec.y, t);
      $[31] = spec.y;
      $[32] = t;
      $[33] = t14;
    } else {
      t14 = $[33];
    }
    let t15;
    if ($[34] !== t14) {
      t15 = {
        display: true,
        text: t14,
        color: muted
      };
      $[34] = t14;
      $[35] = t15;
    } else {
      t15 = $[35];
    }
    let t16;
    let t17;
    if ($[36] === Symbol.for("react.memo_cache_sentinel")) {
      t16 = {
        color: muted
      };
      t17 = {
        color: "rgba(128,128,128,0.15)"
      };
      $[36] = t16;
      $[37] = t17;
    } else {
      t16 = $[36];
      t17 = $[37];
    }
    let t18;
    if ($[38] !== t15) {
      t18 = {
        title: t15,
        ticks: t16,
        grid: t17
      };
      $[38] = t15;
      $[39] = t18;
    } else {
      t18 = $[39];
    }
    let t19;
    if ($[40] !== t13 || $[41] !== t18) {
      t19 = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: t8,
        scales: {
          x: t13,
          y: t18
        }
      };
      $[40] = t13;
      $[41] = t18;
      $[42] = t19;
    } else {
      t19 = $[42];
    }
    const options = t19;
    let t20;
    if ($[43] !== height) {
      t20 = {
        height
      };
      $[43] = height;
      $[44] = t20;
    } else {
      t20 = $[44];
    }
    let t21;
    if ($[45] !== state.rows) {
      t21 = state.rows.map(_temp3);
      $[45] = state.rows;
      $[46] = t21;
    } else {
      t21 = $[46];
    }
    let t22;
    if ($[47] !== t21) {
      t22 = {
        datasets: [{
          data: t21,
          backgroundColor: accent,
          pointRadius: 5
        }]
      };
      $[47] = t21;
      $[48] = t22;
    } else {
      t22 = $[48];
    }
    let t23;
    if ($[49] !== options || $[50] !== t22) {
      t23 = <Scatter options={options} data={t22} />;
      $[49] = options;
      $[50] = t22;
      $[51] = t23;
    } else {
      t23 = $[51];
    }
    let t24;
    if ($[52] !== t20 || $[53] !== t23) {
      t24 = <div style={t20}>{t23}</div>;
      $[52] = t20;
      $[53] = t23;
      $[54] = t24;
    } else {
      t24 = $[54];
    }
    return t24;
  }
  const horizontal = spec.chart === "hbar";
  let t8;
  if ($[55] !== spec.y || $[56] !== t) {
    t8 = axisTitle(spec.y, t);
    $[55] = spec.y;
    $[56] = t;
    $[57] = t8;
  } else {
    t8 = $[57];
  }
  const valueAxisTitle = t8;
  const t9 = horizontal ? "y" : "x";
  let t10;
  if ($[58] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = {
      legend: {
        display: false
      }
    };
    $[58] = t10;
  } else {
    t10 = $[58];
  }
  let t11;
  if ($[59] !== horizontal || $[60] !== valueAxisTitle) {
    t11 = horizontal ? {
      title: {
        display: true,
        text: valueAxisTitle,
        color: muted
      }
    } : {};
    $[59] = horizontal;
    $[60] = valueAxisTitle;
    $[61] = t11;
  } else {
    t11 = $[61];
  }
  const t12 = horizontal ? 0 : 90;
  let t13;
  if ($[62] !== t12) {
    t13 = {
      color: muted,
      maxRotation: t12
    };
    $[62] = t12;
    $[63] = t13;
  } else {
    t13 = $[63];
  }
  let t14;
  if ($[64] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = {
      color: "rgba(128,128,128,0.15)"
    };
    $[64] = t14;
  } else {
    t14 = $[64];
  }
  let t15;
  if ($[65] !== t11 || $[66] !== t13) {
    t15 = {
      ...t11,
      ticks: t13,
      grid: t14
    };
    $[65] = t11;
    $[66] = t13;
    $[67] = t15;
  } else {
    t15 = $[67];
  }
  let t16;
  if ($[68] !== horizontal || $[69] !== valueAxisTitle) {
    t16 = horizontal ? {} : {
      title: {
        display: true,
        text: valueAxisTitle,
        color: muted
      }
    };
    $[68] = horizontal;
    $[69] = valueAxisTitle;
    $[70] = t16;
  } else {
    t16 = $[70];
  }
  let t17;
  let t18;
  if ($[71] === Symbol.for("react.memo_cache_sentinel")) {
    t17 = {
      color: muted
    };
    t18 = {
      color: "rgba(128,128,128,0.15)"
    };
    $[71] = t17;
    $[72] = t18;
  } else {
    t17 = $[71];
    t18 = $[72];
  }
  let t19;
  if ($[73] !== t16) {
    t19 = {
      ...t16,
      ticks: t17,
      grid: t18
    };
    $[73] = t16;
    $[74] = t19;
  } else {
    t19 = $[74];
  }
  let t20;
  if ($[75] !== t15 || $[76] !== t19) {
    t20 = {
      x: t15,
      y: t19
    };
    $[75] = t15;
    $[76] = t19;
    $[77] = t20;
  } else {
    t20 = $[77];
  }
  let t21;
  if ($[78] !== t20 || $[79] !== t9) {
    t21 = {
      indexAxis: t9,
      responsive: true,
      maintainAspectRatio: false,
      plugins: t10,
      scales: t20
    };
    $[78] = t20;
    $[79] = t9;
    $[80] = t21;
  } else {
    t21 = $[80];
  }
  const options_0 = t21;
  let t22;
  if ($[81] !== height) {
    t22 = {
      height
    };
    $[81] = height;
    $[82] = t22;
  } else {
    t22 = $[82];
  }
  let t23;
  if ($[83] !== state.rows) {
    t23 = state.rows.map(_temp4);
    $[83] = state.rows;
    $[84] = t23;
  } else {
    t23 = $[84];
  }
  let t24;
  if ($[85] !== state.rows) {
    t24 = state.rows.map(_temp5);
    $[85] = state.rows;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  let t25;
  if ($[87] !== state.rows) {
    t25 = ctx => resolveColor(state.rows[ctx.dataIndex]?.color ?? BAR_COLOR) || BAR_COLOR;
    $[87] = state.rows;
    $[88] = t25;
  } else {
    t25 = $[88];
  }
  let t26;
  if ($[89] !== t24 || $[90] !== t25) {
    t26 = [{
      data: t24,
      backgroundColor: t25,
      borderRadius: 4,
      barPercentage: 0.85,
      categoryPercentage: 0.9
    }];
    $[89] = t24;
    $[90] = t25;
    $[91] = t26;
  } else {
    t26 = $[91];
  }
  let t27;
  if ($[92] !== t23 || $[93] !== t26) {
    t27 = {
      labels: t23,
      datasets: t26
    };
    $[92] = t23;
    $[93] = t26;
    $[94] = t27;
  } else {
    t27 = $[94];
  }
  let t28;
  if ($[95] !== options_0 || $[96] !== t27) {
    t28 = <Bar options={options_0} data={t27} />;
    $[95] = options_0;
    $[96] = t27;
    $[97] = t28;
  } else {
    t28 = $[97];
  }
  let t29;
  if ($[98] !== t22 || $[99] !== t28) {
    t29 = <div style={t22}>{t28}</div>;
    $[98] = t22;
    $[99] = t28;
    $[100] = t29;
  } else {
    t29 = $[100];
  }
  return t29;
}
function _temp5(r_1) {
  return r_1.value;
}
function _temp4(r_0) {
  return r_0.name;
}
function _temp3(r) {
  return {
    x: r.x ?? 0,
    y: r.value,
    name: r.name
  };
}
function _temp2(item) {
  return `${item.raw && typeof item.raw === "object" && "name" in item.raw ? String((item.raw as {
    name: string;
  }).name) : ""}: (${(item.parsed.x ?? 0).toFixed(2)}, ${(item.parsed.y ?? 0).toFixed(2)})`;
}
function _temp(s) {
  return {
    ...s,
    loading: true
  };
}